import { createSelector } from '@reduxjs/toolkit';
import { LevelStatus } from 'Types';
import type { LevelData, MainAppReducer, TiersByLevels, UserLevelData } from 'Types';
import type {
  RepertoireData,
  RepertoireReducer,
  UserRepertoireProgress,
} from 'Types/RepertoireTypes';

export type CurriculumState = MainAppReducer & RepertoireReducer;
export type PieceState = 'not-started' | 'in-progress' | 'complete';
export type RoadmapPiece = {
  piece: RepertoireData;
  percentComplete: number;
  state: PieceState;
};
export type RepertoireStage = {
  pieces: RoadmapPiece[];
  availability: 'future' | 'available';
  state: 'future' | 'available' | 'in-progress' | 'complete';
  completedCount: number;
  percentComplete: number;
};
export type RoadmapLevelStage = {
  levelNumber: number;
  /** Adapter for the existing Redux selection action; never a level identity. */
  levelIndex: number;
  unitNumber: number;
  level: LevelData;
  lessonState: 'available' | 'active' | 'complete';
  tierCount: number;
  highestTierComplete: number;
  repertoire: RepertoireStage | null;
};

export const isLessonComplete = (progress?: UserLevelData): boolean =>
  progress?.status === LevelStatus.complete || Boolean(progress?.level_completed_at);

export function highestCompletedLesson(progress: UserLevelData[] = []): number {
  return progress.reduce(
    (highest, entry) =>
      entry?.level && isLessonComplete(entry)
        ? Math.max(highest, entry.level.level_number)
        : highest,
    -1,
  );
}

export const selectHighestCompletedLesson = createSelector(
  [(state: MainAppReducer) => state.mainAppReducer.userLevelData],
  highestCompletedLesson,
);

export function pieceProgress(percent?: number | null): {
  percentComplete: number;
  state: PieceState;
} {
  const percentComplete = Number.isFinite(percent) ? Math.min(100, Math.max(0, percent!)) : 0;
  return {
    percentComplete,
    state:
      percentComplete >= 100 ? 'complete' : percentComplete > 0 ? 'in-progress' : 'not-started',
  };
}

export function groupRepertoireByLevel(catalog: RepertoireData[] = []) {
  const byLevel = new Map<number, RepertoireData[]>();
  const unassigned: RepertoireData[] = [];
  for (const piece of catalog) {
    const number = piece.level?.level_number;
    if (number == null || !Number.isInteger(number) || number < 0) {
      unassigned.push(piece);
    } else {
      const pieces = byLevel.get(number) ?? [];
      pieces.push(piece);
      byLevel.set(number, pieces);
    }
  }
  return { byLevel, unassigned };
}

export const selectRepertoireGroups = createSelector(
  [
    (state: RepertoireReducer) => state.repertoireReducer.repertoireData,
    selectHighestCompletedLesson,
  ],
  (catalog, highest) => {
    const { byLevel, unassigned } = groupRepertoireByLevel(catalog);
    const levels = [...byLevel.keys()].sort((a, b) => a - b);
    return {
      byLevel,
      unassigned,
      firstLevel: levels[0] ?? null,
      current: byLevel.get(highest) ?? [],
      previous: levels
        .filter((n) => n < highest)
        .reverse()
        .map((n) => byLevel.get(n)!),
      available: levels
        .filter((n) => n <= highest)
        .reverse()
        .map((n) => byLevel.get(n)!),
      future: levels.filter((n) => n > highest).map((n) => byLevel.get(n)!),
    };
  },
);

export function buildCurriculumGraph(
  levels: LevelData[] = [],
  tiers: TiersByLevels[] = [],
  lessonProgress: UserLevelData[] = [],
  catalog: RepertoireData[] = [],
  repertoireProgress: UserRepertoireProgress[] = [],
): RoadmapLevelStage[] {
  const highest = highestCompletedLesson(lessonProgress);
  const { byLevel } = groupRepertoireByLevel(catalog);
  const lessons = new Map(
    lessonProgress.filter((p) => p?.level).map((p) => [p.level.level_number, p]),
  );
  const tiersByLevel = new Map(tiers.map((t) => [t.level_number, t.tiers.length]));
  // First record wins, matching the repertoire cards and browse filter.
  const progress = new Map<number, UserRepertoireProgress>();
  repertoireProgress.forEach((p) => {
    if (p.repertoire && !progress.has(p.repertoire.id)) progress.set(p.repertoire.id, p);
  });
  return levels
    .map((level, levelIndex): RoadmapLevelStage => {
      const number = level.level_number;
      const lesson = lessons.get(number);
      const assigned = byLevel.get(number);
      let repertoire: RepertoireStage | null = null;
      if (assigned?.length) {
        const pieces = assigned.map((piece) => ({
          piece,
          ...pieceProgress(progress.get(piece.id)?.percent_complete),
        }));
        const completedCount = pieces.filter((p) => p.state === 'complete').length;
        const future = number > highest;
        repertoire = {
          pieces,
          availability: future ? 'future' : 'available',
          state: future
            ? 'future'
            : completedCount === pieces.length
              ? 'complete'
              : pieces.some((p) => p.percentComplete > 0)
                ? 'in-progress'
                : 'available',
          completedCount,
          percentComplete: pieces.reduce((sum, p) => sum + p.percentComplete, 0) / pieces.length,
        };
      }
      return {
        levelNumber: number,
        levelIndex,
        unitNumber: level.unit?.unit_number,
        level,
        lessonState: isLessonComplete(lesson) ? 'complete' : lesson ? 'active' : 'available',
        tierCount: number === 0 ? 0 : (tiersByLevel.get(number) ?? 1),
        highestTierComplete: Math.max(0, lesson?.highest_tier_complete ?? 0),
        repertoire,
      };
    })
    .sort((a, b) => a.levelNumber - b.levelNumber);
}

export const selectCurriculumGraph = createSelector(
  [
    (state: CurriculumState) => state.mainAppReducer.levelData,
    (state: CurriculumState) => state.mainAppReducer.tiersByLevels,
    (state: CurriculumState) => state.mainAppReducer.userLevelData,
    (state: CurriculumState) => state.repertoireReducer.repertoireData,
    (state: CurriculumState) => state.repertoireReducer.userRepertoireProgresses,
  ],
  buildCurriculumGraph,
);

export function parseRepertoireLevel(value: string | null): number | null {
  return value !== null && /^\d+$/.test(value) && Number.isSafeInteger(Number(value))
    ? Number(value)
    : null;
}
